// Research Engine — domain badges. Labels come from i18n.
import { useTranslation } from "react-i18next";
import type { InputType, UserRole } from "@re/shared";
import { Badge } from "./primitives";
import { Icon, type IconName } from "./icon";

const SOURCE_ICON: Record<InputType, IconName> = {
  youtube: "youtube",
  arxiv: "arxiv",
  github: "github",
  huggingface: "beaker",
  blog: "file-text",
  community: "users",
  topic: "compass",
};

/** Source-type badge keyed off the real InputType union. */
export function SourceBadge({
  type, size = "sm", showLabel = true,
}: {
  type: InputType;
  size?: "xs" | "sm" | "md";
  showLabel?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <Badge tone="line" size={size} icon={SOURCE_ICON[type]}>
      {showLabel && t(`source.${type}`)}
    </Badge>
  );
}

/** Bare source icon (for list rows / source list). */
export function SourceIcon({ type, size = 14 }: { type: InputType; size?: number }) {
  return <Icon name={SOURCE_ICON[type]} size={size} />;
}

/** Free vs Pro tier badge. role "paid"/"admin" → Pro. */
export function TierBadge({ role, tier }: { role?: UserRole; tier?: "free" | "paid" }) {
  const { t } = useTranslation();
  const isPaid = tier ? tier === "paid" : role ? role !== "free" : false;
  return (
    <Badge tone={isPaid ? "accent" : "neutral"} size="xs">
      {isPaid ? t("common.pro") : t("common.free")}
    </Badge>
  );
}

export function VisibilityPill({
  visibility, size = "sm",
}: {
  visibility: "public" | "private";
  size?: "xs" | "sm" | "md";
}) {
  const { t } = useTranslation();
  return (
    <Badge tone={visibility === "public" ? "ok" : "neutral"} size={size} icon={visibility === "public" ? "globe" : "lock"}>
      {visibility === "public" ? t("common.public") : t("common.private")}
    </Badge>
  );
}
